import { useCallback } from 'react';
import { LinkTypeEnum } from '@hqo/hqo-miniapp-client-sdk';

import { useMiniappSdk } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';
import { useHostInfo } from 'store/host-info/hooks/use-host-info';

/**
 * The destination token the host resolves to its own bookings-list route (RES-1367).
 *
 * A NAME, never a path. The host owns where its bookings list lives and fills in the building and
 * utility button from the route it is already on, which is knowledge this app does not have and
 * should not have to carry. Mirrored in tenant-portal's `RESOURCE_BOOKING_BOOKINGS_NAV_VALUE`.
 */
export const BOOKINGS_LIST_NAV_VALUE = 'RESOURCE_BOOKING_BOOKINGS';

/** RES-1407 — the token for ONE booking, named by transaction uuid. Mirrors tenant-portal's `RESOURCE_BOOKING_BOOKING_DETAILS_NAV_VALUE`. */
export const BOOKING_DETAILS_NAV_VALUE = 'RESOURCE_BOOKING_BOOKING_DETAILS';

/**
 * Asks the HOST to go to the tenant's bookings list.
 *
 * Deliberately not in-app routing. Moving this frame alone leaves the host's url pointing at the one
 * booking the reader was looking at, so a reload would drop them straight back into it -- the list
 * would be a place they could reach but never stay. Only the host can move its own url, and
 * `redirectToContent` is the door that already exists for asking.
 *
 * A host that does not know the token does nothing with it, which is why the control that calls this
 * is only rendered where it can land.
 */
export const useRedirectToBookings = (): VoidFunction => {
  const client = useMiniappSdk();

  return useCallback(() => {
    client?.redirectToContent({ type: LinkTypeEnum.NATIVE_SCREEN, value: BOOKINGS_LIST_NAV_VALUE });
  }, [client]);
};

/**
 * RES-1407 — asks the host to show one booking, or the list when there is no uuid to name.
 *
 * ⛔ TENANT PORTAL ONLY. The native apps don't know these tokens, so on every other host this is a no-op.
 */
export const useRedirectHostToBooking = (): ((transactionUuid?: string) => void) => {
  const client = useMiniappSdk();
  const { isTenantPortalHost } = useHostInfo();

  return useCallback(
    (transactionUuid?: string) => {
      if (!isTenantPortalHost) return;

      client?.redirectToContent(
        transactionUuid
          ? { type: LinkTypeEnum.NATIVE_SCREEN, value: BOOKING_DETAILS_NAV_VALUE, options: { uuid: transactionUuid } }
          : { type: LinkTypeEnum.NATIVE_SCREEN, value: BOOKINGS_LIST_NAV_VALUE },
      );
    },
    [client, isTenantPortalHost],
  );
};
